import Dropzone, { FileRejection } from 'react-dropzone';
import { Icon } from '@vechaiui/react';
import { XIcon } from '@heroicons/react/solid';
import { useEffect } from 'react';
import { useField } from 'formik';

import Field from '#/components/forms/Field';
import { isRequiredYupField, useYupRequiredFields } from '#/components/forms/Formik';

type SpecialFile = File & { preview: string };

export const DropzoneFormik: React.FC<DropzoneFormikProps> = ({
  accept,
  children: _children,
  content,
  label,
  name,
  subtext,
}) => {
  const [field, meta, helper] = useField<SpecialFile[] | undefined>(name);
  const requiredFields = useYupRequiredFields();
  const isRequired = isRequiredYupField(requiredFields, name);
  const showError = meta.touched && !!meta.error;

  const handleDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      helper.setValue(
        acceptedFiles.map((file) => Object.assign(file, { preview: URL.createObjectURL(file) })),
      );
    }
  };
  const handleDropRejected = (fileRejections: FileRejection[]) => {
    // TODO: refine the message
    alert('Invalid file type detected, please input only [Images]');
    helper.setValue(undefined);
  };

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => {
      field.value?.forEach((file: SpecialFile) => {
        URL.revokeObjectURL(file.preview);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Field name={name}>
      {label && (
        <label className="uppercase mb-1 inline-block" htmlFor={`input-${name}`}>
          {label} {isRequired && <span className="text-red-600">*</span>}
        </label>
      )}
      {/* TODO: How to click to preview in bigger size or edit single image? */}
      <Dropzone onDrop={handleDrop} accept={accept} onDropRejected={handleDropRejected}>
        {({ getRootProps, getInputProps }) => (
          <section id={`input-${name}`}>
            <div
              {...getRootProps()}
              className="rounded-lg bg-gray-50 p-5 border-dashed border-2 hover:cursor-pointer hover:bg-gray-100 hover:border-gray-300"
            >
              <input {...getInputProps()} />
              <p className="text-center">{content}</p>
              {!!subtext && (
                <div className="text-center">
                  <em className="text-gray-400">{subtext}</em>
                </div>
              )}
            </div>
            <aside className="grid grid-cols-7 mt-4 gap-2">
              {field.value?.map((file: SpecialFile) => (
                <div
                  className="rounded-sm border border-slate-200 aspect-square p-1 relative"
                  key={file.name}
                >
                  <span
                    className="absolute right-0 top-0 p-1 cursor-pointer"
                    onClick={() => {
                      const newValue = field.value?.filter((f) => f.name !== file.name);
                      helper.setValue(!!newValue && newValue.length > 0 ? newValue : undefined);
                    }}
                  >
                    <Icon as={XIcon} label="delete" className="w-3 h-3 text-red-600 block" />
                  </span>

                  <img
                    className="block object-contain w-full h-full"
                    src={file.preview}
                    // Revoke data uri after image is loaded
                    onLoad={() => {
                      URL.revokeObjectURL(file.preview);
                    }}
                  />
                </div>
              ))}
            </aside>
          </section>
        )}
      </Dropzone>
    </Field>
  );
};
export type DropzoneFormikProps = {
  label?: string;
  accept: { [key: string]: string[] };
  content: string;
  subtext?: string;
  name: string;
};
